const BASE_URL = "/api";
//const BASE_URL = "http://localhost:9092/api";

const request = async (endpoint, options = {}) => {
  // Reads JWT token from sessionStorage
  const token = sessionStorage.getItem("token"); 
  
  const headers = {
    // Automatically attaches token to ALL requests
    ...(token && { "Authorization": `Bearer ${token}` }),
    ...options.headers,
  };

  // Only set 'Content-Type' if the body is NOT FormData.
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = headers["Content-Type"] || "application/json";
  }

  const config = {
    ...options,
    headers,
  };

  const url = `${BASE_URL.replace(/\/$/, "")}/${endpoint.replace(/^\//, "")}`;

  const response = await fetch(url, config);

  if (response.status === 401) {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("roleId");
    sessionStorage.removeItem("clientName");
    sessionStorage.removeItem("clientId");
    window.location.href = "/login";
    throw new Error("Session expired. Please log in again.");
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const apiService = {
  // ── Authentication ──
  login: (credentials) => request("/auth/login", { method: "POST", body: JSON.stringify(credentials) }),

  // ── Seats Endpoints ──
  getDashboardStats: () => request("/seats/dashboard-stats"),
  getSeats: () => request("/seats"),
  getAllSeats: () => request("/seats"),
  getSeatByCode: (code) => request(`/seats/search?code=${encodeURIComponent(code)}`),
  createSeat: (seatData) => request("/seats", { method: "POST", body: JSON.stringify(seatData) }),
  updateSeatConfig: (seatId, seatData) => request(`/seats/${seatId}`, {
    method: "PUT",
    body: JSON.stringify(seatData)
  }),

  // Seat Assignments
  getAdmissionsBySeat: (seatId) => request(`/admissions/by-seat/${seatId}`),
  bookSeat: (seatId) => request(`/seats/${seatId}/book`, { method: "PUT" }),

  // ── Student Endpoints ──
  createStudent: (studentData) => request("/students", { method: "POST", body: JSON.stringify(studentData) }),
  updateStudentProfile: (studentId, studentData) => request(`/students/${studentId}`, {
    method: "PUT",
    body: JSON.stringify(studentData),
  }),

  // ── Admission Endpoints ──
  createAdmission: (admissionData) => request("/admissions", { method: "POST", body: JSON.stringify(admissionData) }),
  searchAdmissions: (query) => request(`/admissions/search?value=${encodeURIComponent(query)}`),
  getAllAdmissions: () => request("/admissions"),
  
  // Renewals & Amendments
  renewAdmission: (admissionId, endDate, feesPaid) => request(`/admissions/renew/${admissionId}?endDate=${endDate}&fees=${feesPaid}`, {
    method: "PUT"
  }),
  amendAdmissionDetails: (admissionId, admissionData) => request(`/admissions/${admissionId}/amend-student`, {
    method: "PUT",
    body: JSON.stringify(admissionData),
  }),
  amendSeatAssignment: (admissionId, seatId) => request(`/admissions/${admissionId}/amend-seat/${seatId}`, {
    method: "PUT"
  }),
  getRenewAdmissionList: (days) => request(`/admissions/renew-list?days=${days}`),

  // ── Email & Reports ──
  emailRenewList: (formData) => request("/email/renew-list", { 
    method: "POST", 
    body: formData 
  }),
  
  emailReport: (formData) => request("/email/renew-list", { method: "POST", body: formData }),
  sendBroadcastEmail: (emailPayload) => request("/email/broadcast", {
    method: "POST",
    body: JSON.stringify(emailPayload)
  }),

  // ── Admissions Report Endpoints ──
  getAdmissionsReport: (params) => {
    const queryParams = new URLSearchParams({
      fromDate: params.fromDate || "",
      toDate: params.toDate || "",
      active: params.active,
      inactive: params.inactive
    });
    return request(`/admissions/reports/?${queryParams.toString()}`);
  },

  emailAdmissionsReport: (formData) => request("/email/students/admissionreports", {
    method: "POST",
    body: formData
  }),

  // ── 🆕 Corrected Student Enquiry Endpoints ──
  createEnquiry: (enquiryData) => request("/enquiries", {
    method: "POST",
    body: JSON.stringify(enquiryData)
  }),

  // Updated to match Spring Boot @GetMapping("/api/enquiries")
  // Automatically passes token, query parameters for date filters only
  getEnquiries: (params = {}) => {
    const { fromDate, toDate } = params;
    const queryParams = new URLSearchParams();
    
    if (fromDate) queryParams.append("fromDate", fromDate);
    if (toDate) queryParams.append("toDate", toDate);
    
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
    return request(`/enquiries${queryString}`);
  },

  updateEnquiryStatus: (enquiryId, status) => request(`/enquiries/${enquiryId}/status?status=${encodeURIComponent(status)}`, {
    method: "PUT"
  }),

  // Matches Spring Boot @PostMapping("/api/enquiries/email-report")
  emailEnquiryReport: (formData) => request("/email/email-report", {
    method: "POST",
    body: formData
  })
};